import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { debounceTime, withLatestFrom, map, catchError, of, switchMap } from 'rxjs';
import { MileagePlanActivityLookupStatus } from '../../../dtos/response/mileage-plan-activity-response/mileage-plan-activity-lookup-status';
import { RootState } from '../../../state/state';
import { MileagePlanActivityService } from '../mileage-plan-activity.service';
import { getMileagePlanProfileSearchMileagePlanNumber } from '../../../state/mileage-plan-profile-search/mileage-plan-profile-search.selectors';
import { MileagePlanActivityLookupResponse } from '../../../dtos/response/mileage-plan-activity-response/mileage-plan-activity-lookup-response';
import {
  mileagePlanActivitySearch,
  mileagePlanActivitySearchFailure,
  mileagePlanActivitySearchSuccess,
} from './mileage-plan-activity-service.actions';
import { MileagePlanActivityRange } from '../../../dtos/request/mileage-plan-activity-request/mileage-plan-activity-range';
import { MileagePlanActivityServiceSelectors } from './mileage-plan-activity-service.selectors';

@Injectable()
export class MileagePlanActivityServiceEffects {
  private readonly actions$ = inject(Actions);
  private readonly mileagePlanActivityService = inject(MileagePlanActivityService);
  private readonly store = inject<Store<RootState>>(Store);

  mileagePlanActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(mileagePlanActivitySearch),
      debounceTime(300),
      withLatestFrom(
        this.store.select(getMileagePlanProfileSearchMileagePlanNumber),
        this.store.select(MileagePlanActivityServiceSelectors.getActivities)
      ),
      switchMap(([action, mileagePlanNumber, activities]) => {
        return this.mileagePlanActivityService
          .getMileagePlanActivities(mileagePlanNumber, action.range, action.range === MileagePlanActivityRange.twentyFourMonths)
          .pipe(
            map((result) => {
              result.currentActivityRange = action.range;

              if (result.status === MileagePlanActivityLookupStatus.SUCCESS) {
                return mileagePlanActivitySearchSuccess(result);
              } else if (result.status === MileagePlanActivityLookupStatus.NO_DATA_RETURNED) {
                result.details.activities = [];
                if (action.range === MileagePlanActivityRange.threeMonths) {
                  result.noActivitiesInThreeMonths = true;
                  return mileagePlanActivitySearchSuccess(result);
                } else if (action.range === MileagePlanActivityRange.twentyFourMonths) {
                  result.noActivitiesInTwentyFourMonths = true;
                  return mileagePlanActivitySearchSuccess(result);
                }
              }

              result.details = { activities };
              return mileagePlanActivitySearchFailure(result);
            }),
            catchError(() => {
              const response: MileagePlanActivityLookupResponse = {
                status: MileagePlanActivityLookupStatus.UNCAUGHT,
                currentActivityRange: action.range,
                mileagePlanNumber,
              };
              return of(mileagePlanActivitySearchFailure(response));
            })
          );
      })
    )
  );
}
